import { Button, Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { DEFAULT_OPERATION } from "@/pages/Buscar/useSearchState";

// Order matches the homepage default (Comprar) → primary intent first.
// "Todas" sits last as the catch-all/reset, not the lead — mirrors the
// homepage operation menu order and avoids the visual collision where a
// user lands with "Comprar" selected but "Todas" is the first chip.
export const OPERATION_OPTIONS = [
    { value: "SALE", label: "Comprar" },
    { value: "LEASE", label: "Rentar" },
    { value: "PRESALE", label: "Preventa" },
    { value: "ALL", label: "Todas" },
];

// "Todas" expands to all 3 ops internally; useSearchState strips that as the
// default. "ALL" is also what we surface when the committed state has 0 or 3
// values (both are the all-selected case).
export const TODO_OPERATION = ["SALE", "LEASE", "PRESALE"];

export const currentOperationValue = (operation) => {
    if (!operation || operation.length === 0 || operation.length === 3) return "ALL";
    if (operation.length === 1) return operation[0];
    return "ALL";
};

// Hook used by the bottom drawer at xs — owns a draft array and exposes
// commit/reset/isDirty in the same shape as the other section hooks so
// MoreFiltersPopover's global Aplicar/Limpiar can treat all sections
// uniformly.
export const useOperationDraft = (state) => {
    const [draftOperation, setDraftOperation] = useState(state.operation);

    useEffect(() => {
        setDraftOperation(state.operation);
    }, [state.operation]);

    const draftValue = currentOperationValue(draftOperation);
    const committedValue = currentOperationValue(state.operation);
    const isDirty = draftValue !== committedValue;

    // "Todas" maps to [] (the true default) — not the length-3 expansion.
    // Both render identically via currentOperationValue, but [] is what
    // isDefault recognizes, so explicit Todas picks strip from URL instead
    // of polluting it with operacion=SALE,LEASE,PRESALE. Single op picks
    // produce length-1 arrays so the trigger highlight check (length === 1)
    // can distinguish "active filter" from "back to default."
    const onPick = (value) => {
        if (value === "ALL") setDraftOperation([]);
        else setDraftOperation([value]);
    };

    const commit = (setFilter, _clearFilter) => {
        setFilter("operation", draftOperation);
    };

    // D18.a: "Limpiar todos" in the drawer resets each section to its default.
    // For operation that's now Comprar, not the empty "no filter" state. Users
    // who want Todas explicitly pick it via the segmented.
    const reset = (setFilter, _clearFilter) => {
        setDraftOperation([DEFAULT_OPERATION]);
        setFilter("operation", [DEFAULT_OPERATION]);
    };

    return {
        draftOperation,
        draftValue,
        onPick,
        isDirty,
        canCommit: true,
        commit,
        reset,
    };
};

// Pure body for use inside the drawer. Mirrors TopBar's OperationSegmented
// look-and-feel.
export const OperationFilterBody = ({ draft }) => {
    const { draftValue, onPick } = draft;
    return (
        <Flex borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" h="40px">
            {OPERATION_OPTIONS.map((opt) => {
                const active = opt.value === draftValue;
                return (
                    <Button
                        key={opt.value}
                        onClick={() => onPick(opt.value)}
                        flex={1}
                        h="40px"
                        px={4}
                        fontSize="sm"
                        fontWeight={active ? 600 : 500}
                        bg={active ? "colorPalette.solid" : "white"}
                        color={active ? "white" : "gray.700"}
                        borderRadius={0}
                        _hover={{ bg: active ? "colorPalette.solid" : "gray.50" }}
                    >
                        {opt.label}
                    </Button>
                );
            })}
        </Flex>
    );
};
